require('./bootstrap');

import { createApp } from 'vue';
import VueSweetalert2 from 'vue-sweetalert2';
import 'sweetalert2/dist/sweetalert2.min.css';

// -----------------------------------------------------------------
// --- Import plugins
// -----------------------------------------------------------------
import store from './store/index';
import vuetify from './plugin/vuetify';
import moment from './plugin/moment';

// -----------------------------------------------------------------
// --- Import routers
// -----------------------------------------------------------------
import backRouter from './router/backoffice';

// -----------------------------------------------------------------
// --- Import layouts
// -----------------------------------------------------------------
import BackofficeLayout from './layouts/BackofficeLayout.vue';

// -----------------------------------------------------------------
// --- Helpers globaux ($xxx) partagés par tous les composants
// -----------------------------------------------------------------

const getStepColor = (step) => {
    switch (step) {
        case 'CREATED':
            return 'indigo';
        case 'VERIF_CONF':
            return 'purple';
        case 'PRINTING':
            return 'deep-purple';
        case 'VERIF_PRE_SIGN':
            return 'blue';
        case 'SIGN':
            return 'light-blue';
        case 'VERIF_POST_SIGN':
            return 'cyan';
        case 'RECEPTION':
            return 'teal';
        case 'DELIVERING':
            return 'teal-darken-4';
        case 'FINISHED':
            return 'green';
    }
};

const getStepText = (step) => {
    switch (step) {
        case 'CREATED':
            return 'Initialiser';
        case 'VERIF_CONF':
            return 'Vérification de conformité';
        case 'PRINTING':
            return 'Impression';
        case 'VERIF_PRE_SIGN':
            return 'Vérification DAJ avant signature';
        case 'SIGN':
            return 'Signature';
        case 'VERIF_POST_SIGN':
            return 'Vérification DAJ aprés signature';
        case 'RECEPTION':
            return 'Réception';
        case 'DELIVERING':
            return 'En cours de retrait des diplomes';
        case 'FINISHED':
            return 'Terminer';
    }
};

const globalHelpers = {
    $shortDateFormat: (date) => moment(date).format('Do MMM YYYY'),

    $guardTLD: async (to, from, next) => {
        let host = window.location.hostname;
        if (host[host.length - 1] === '.') {
            window.location.host = host.slice(0, -1);
        }
        next();
    },

    $shortDateTimeFormat: (date) => moment(date).format('Do MMM YYYY à HH:mm'),

    $littleDateFormat: (date) => moment(date).format('DD/MM/YYYY HH:mm'),

    $longDateFormat: (date, end = false) =>
        end ? `${moment(date).format('dddd Do MMMM YYYY')} à 00h 00` : moment(date).format('dddd Do MMMM YYYY'),

    $truncateString: (str, length) => {
        if (str == null) return '';
        return str.length <= length ? str : str.substring(0, length) + ' ...';
    },

    $getEtatColor: (diplome, demande) => {
        if ([undefined, null].includes(demande) && diplome?.statut == 'FAILED') {
            return 'red';
        } else {
            return getStepColor(demande?.statut ?? diplome?.active_step);
        }
    },

    $getEtatText: (diplome, demande) => {
        if ([undefined, null].includes(demande) && diplome?.statut == 'FAILED') {
            return 'Rejeter';
        } else {
            return getStepText(demande?.statut ?? diplome?.active_step);
        }
    },

    $statutDemandeIndiv: (step) => {
        switch (step) {
            case 'created':
                return 'En cours';
            case 'printed':
                return 'Éditée';
            case 'delivered':
                return 'Délivrée';
            case 'rejected':
                return 'Rejetée';
            default:
                return 'Initialiser';
        }
    },

    $colorDemandeIndiv: (step) => {
        switch (step) {
            case 'created':
                return 'amber';
            case 'printed':
                return 'teal';
            case 'delivered':
                return 'green';
            case 'rejected':
                return 'red';
            default:
                return '#283593';
        }
    },

    $iconDemandeIndiv: (step) => {
        switch (step) {
            case 'created':
                return 'mdi-information-outline';
            case 'printed':
                return 'mdi-progress-check';
            case 'delivered':
                return 'mdi-check-circle-outline';
            case 'rejected':
                return 'mdi-close-circle-outline';
            default:
                return 'mdi-information-outline';
        }
    },

    $stepDemandeIndiv: (step) => {
        switch (step) {
            case 'created':
                return 1;
            case 'printed':
                return 2;
            case 'delivered':
                return 3;
            case 'rejected':
                return 0;
            default:
                return 1;
        }
    },

    $getEtatTable: (verif, successText) => {
        if (verif != null) {
            return verif.statut == 'ACCEPTED' ? successText : 'Rejeter';
        } else {
            return 'En attente';
        }
    },

    $statutDemande: (demande) => {
        if (demande.is_reject) {
            return 'Rejeter';
        }
        return getStepText(demande.statut);
    },

    $getNextStep: (demande) => {
        switch (demande.statut) {
            case 'CREATED':
                return 'PRINTING';
            case 'PRINTING':
                return 'VERIF_CONF';
            case 'VERIF_CONF':
                return 'VERIF_PRE_SIGN';
            case 'VERIF_PRE_SIGN':
                return 'VERIF_POST_SIGN';
            case 'VERIF_POST_SIGN':
                return 'RECEPTION';
            case 'RECEPTION':
                return 'DELIVERING';
            case 'DELIVERING':
                return 'FINISHED';
            default:
                return demande.statut;
        }
    }
};

// -----------------------------------------------------------------
// --- Create App
// -----------------------------------------------------------------
if (document.getElementById('back-office')) {
    axios.defaults.baseURL = process.env.MIX_API_BACKOFFICE || '/backoffice';

    const app = createApp(BackofficeLayout);

    Object.assign(app.config.globalProperties, globalHelpers);

    app.use(store);
    app.use(backRouter);
    app.use(vuetify);
    app.use(VueSweetalert2);

    app.mount('#back-office');
}
